
import { useState, useEffect, useCallback } from 'react';
import type { Recipe } from '../types';
import { subscribeToRecipe, updateRecipe } from '../services/firebaseService';
import { useAuth } from './useAuth';

export const useRecipeManager = () => {
  const [recipe, setRecipe] = useState<Recipe>({ principles: [], generalTools: [], specificTools: {} });
  const { user } = useAuth();

  useEffect(() => {
    const unsubscribe = subscribeToRecipe(user?.uid, (newRecipe) => {
      const validatedRecipe: Recipe = {
        principles: newRecipe.principles || [],
        generalTools: newRecipe.generalTools || (newRecipe as any).tools || [],
        specificTools: newRecipe.specificTools || {},
      };
      setRecipe(validatedRecipe);
    });
    return () => unsubscribe();
  }, [user?.uid]);

  const saveRecipe = useCallback(async (newRecipe: Recipe) => {
    if (user?.uid) {
        await updateRecipe(user.uid, newRecipe);
    }
  }, [user?.uid]);

  // Optimistic updates: Update state immediately, then save to server.
  // Subscriptions will ensure eventual consistency.

  // --- Principles ---
  const addPrinciple = useCallback((principle: string) => {
    if (principle.trim()) {
      const newRecipe = { ...recipe, principles: [...recipe.principles, principle.trim()] };
      setRecipe(newRecipe); // Optimistic update
      saveRecipe(newRecipe);
    }
  }, [recipe, saveRecipe]);

  const updatePrinciple = useCallback((index: number, newText: string) => {
    if (newText.trim()) {
      const updatedPrinciples = [...recipe.principles];
      updatedPrinciples[index] = newText.trim();
      const newRecipe = { ...recipe, principles: updatedPrinciples };
      setRecipe(newRecipe); // Optimistic update
      saveRecipe(newRecipe);
    }
  }, [recipe, saveRecipe]);

  const deletePrinciple = useCallback((index: number) => {
    const updatedPrinciples = recipe.principles.filter((_, i) => i !== index);
    const newRecipe = { ...recipe, principles: updatedPrinciples };
    setRecipe(newRecipe); // Optimistic update
    saveRecipe(newRecipe);
  }, [recipe, saveRecipe]);

  // --- General Tools ---
  const addGeneralTool = useCallback((tool: string) => {
    if (tool.trim()) {
      const newRecipe = { ...recipe, generalTools: [...recipe.generalTools, tool.trim()] };
      setRecipe(newRecipe); // Optimistic update
      saveRecipe(newRecipe);
    }
  }, [recipe, saveRecipe]);

  const updateGeneralTool = useCallback((index: number, newText: string) => {
    if (newText.trim()) {
      const updatedTools = [...recipe.generalTools];
      updatedTools[index] = newText.trim();
      const newRecipe = { ...recipe, generalTools: updatedTools };
      setRecipe(newRecipe); // Optimistic update
      saveRecipe(newRecipe);
    }
  }, [recipe, saveRecipe]);

  const deleteGeneralTool = useCallback((index: number) => {
    const updatedTools = recipe.generalTools.filter((_, i) => i !== index);
    const newRecipe = { ...recipe, generalTools: updatedTools };
    setRecipe(newRecipe); // Optimistic update
    saveRecipe(newRecipe);
  }, [recipe, saveRecipe]);
  
  // --- Specific Tools ---
  const addSpecificTool = useCallback((path: string, tool: string) => {
    if (tool.trim() && path) {
      const newSpecificTools = { ...recipe.specificTools };
      const toolsForPath = [...(newSpecificTools[path] || []), tool.trim()];
      newSpecificTools[path] = toolsForPath;
      const newRecipe = { ...recipe, specificTools: newSpecificTools };
      setRecipe(newRecipe); // Optimistic update
      saveRecipe(newRecipe);
    }
  }, [recipe, saveRecipe]);

  const updateSpecificTool = useCallback((path: string, index: number, newText: string) => {
    if (newText.trim() && path) {
      const newSpecificTools = { ...recipe.specificTools };
      const toolsForPath = [...(newSpecificTools[path] || [])];
      if (index >= 0 && index < toolsForPath.length) {
        toolsForPath[index] = newText.trim();
        newSpecificTools[path] = toolsForPath;
        const newRecipe = { ...recipe, specificTools: newSpecificTools };
        setRecipe(newRecipe); // Optimistic update
        saveRecipe(newRecipe);
      }
    }
  }, [recipe, saveRecipe]);
  
  const deleteSpecificTool = useCallback((path: string, index: number) => {
    if (path) {
        const newSpecificTools = { ...recipe.specificTools };
        const toolsForPath = [...(newSpecificTools[path] || [])];
        if (index >= 0 && index < toolsForPath.length) {
            const updatedTools = toolsForPath.filter((_, i) => i !== index);
            if (updatedTools.length > 0) {
              newSpecificTools[path] = updatedTools;
            } else {
              delete newSpecificTools[path];
            }
            const newRecipe = { ...recipe, specificTools: newSpecificTools };
            setRecipe(newRecipe); // Optimistic update
            saveRecipe(newRecipe);
        }
    }
  }, [recipe, saveRecipe]);

  return { 
    recipe, 
    addPrinciple, updatePrinciple, deletePrinciple, 
    addGeneralTool, updateGeneralTool, deleteGeneralTool,
    addSpecificTool, updateSpecificTool, deleteSpecificTool
  };
};
